import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/store";
import { toast } from "react-toastify";

import { LOGINPAGE } from "../../auth/routes";
import { OPERACOES_PAGE } from "../../operacoes/routes";

import "./SlotsManagerPage.css";

import searchSvg from "../../../img/search.svg";
import DenseForest from "../../../img/dense-forest.jpg";

import { getStoresPaginatedApi, StorePublic } from "../../store/storeApi";
import {
  adminCreateRegistrationsApi,
  createSlotShareLinkApi,
  createSlotsApi,
  deleteSlotApi,
  listAllRegistrationsApi,
  listAllSlotsApi,
  listStoreRegistrationsApi,
  listStoreSlotsApi,
  updateRegistrationStatusApi,
  updateSlotApi,
  type SlotRegistrationStatus,
  type StoreSlot,
  type StoreSlotWithAvailability,
  type UpdateRegistrationStatusDto,
} from "../schedulingApi";

import { getFreelancersControlApi } from "../../freelancer-control/freelancerControlApi";

type StoreSlotRow = StoreSlotWithAvailability & {
  store?: {
    nomeFantasia?: string;
  };
};

const STORES_LIMIT = 10;
const FREELANCERS_LIMIT = 5;

type AllStoresOption = {
  id: null;
  nomeFantasia: "Todas as Lojas";
  endereco?: undefined;
  estado?: undefined;
  isAll: true;
};

type StoreOption = StorePublic | AllStoresOption;

const ALL_STORES_OPTION: AllStoresOption = {
  id: null,
  nomeFantasia: "Todas as Lojas",
  endereco: undefined,
  estado: undefined,
  isAll: true,
};

type DraftSlot = {
  day: string;
  startAt: string;
  endAt: string;
  capacity: number;
};

type FreelancerPublic = {
  id: string;
  nomeCompleto?: string;
  cpf?: string;
};

type PaginatedFreelancersResponse = {
  items: FreelancerPublic[];
  total?: number;
  totalItens?: number;
};

function isRealStore(store: StoreOption | null): store is StorePublic {
  return !!store && typeof store.id === "string";
}

function isAllStores(store: StoreOption | null): store is AllStoresOption {
  return !!store && store.id === null;
}

function rawToken(t?: string | null) {
  if (!t) return "";
  return t.startsWith("Bearer ") ? t.slice(7) : t;
}

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatBrDate(ymd: string): string {
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
}

function startOfWeekMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function addDaysYmd(ymd: string, daysToAdd: number): string {
  const dt = new Date(`${ymd}T00:00:00`);
  dt.setDate(dt.getDate() + daysToAdd);
  return toYmd(dt);
}

function minutesFromHHMM(hhmm: string): number {
  const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
  return h * 60 + m;
}

function hhmmFromMinutes(totalMinutes: number): string {
  const mins = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
  const h = String(Math.floor(mins / 60)).padStart(2, "0");
  const m = String(mins % 60).padStart(2, "0");
  return `${h}:${m}`;
}

function buildIsoUtcRange(dayYmd: string, hhmm: string): string {
  return new Date(`${dayYmd}T${hhmm}:00`).toISOString();
}

function buildIsoLocalOffset(dayYmd: string, hhmm: string): string {
  return `${dayYmd}T${hhmm}:00-03:00`;
}

function ymdFromIsoOrYmd(s: string): string {
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function formatTimeFromIso(iso: string) {
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function copyToClipboard(text: string) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  return new Promise<void>((resolve, reject) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      ta.style.top = "-9999px";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (!ok) reject(new Error("Falha ao copiar"));
      else resolve();
    } catch (e) {
      reject(e);
    }
  });
}

const OpenSlotsDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  store: StorePublic;
  token: string;
  bearer: string;
  daysOfWeek: { label: string; ymd: string }[];
  initialDayYmd: string;
  onCreated: () => void;
}> = ({ isOpen, onClose, store, token, bearer, daysOfWeek, initialDayYmd, onCreated }) => {
  const [selectedDayYmd, setSelectedDayYmd] = useState(initialDayYmd);

  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("18:00");
  const [endManuallyEdited, setEndManuallyEdited] = useState(false);
  const [capacity, setCapacity] = useState(1);

  const [draftSlots, setDraftSlots] = useState<DraftSlot[]>([]);
  const [saving, setSaving] = useState(false);

  const [showFreelancers, setShowFreelancers] = useState(false);
  const [freelancersPage, setFreelancersPage] = useState(1);
  const [freelancersTotal, setFreelancersTotal] = useState(0);
  const [freelancers, setFreelancers] = useState<FreelancerPublic[]>([]);
  const [loadingFreelancers, setLoadingFreelancers] = useState(false);
  const [selectedFreelancerIds, setSelectedFreelancerIds] = useState<Set<string>>(new Set());
  const [qInput, setQInput] = useState("");
  const [qApplied, setQApplied] = useState("");

  const selectedCount = selectedFreelancerIds.size;

  const freelancersTotalPages = useMemo(
    () => Math.max(1, Math.ceil(freelancersTotal / FREELANCERS_LIMIT)),
    [freelancersTotal],
  );

  const fetchFreelancersByStore = useCallback(
    async (page: number) => {
      if (!token) return;
      try {
        setLoadingFreelancers(true);

        const data = await getFreelancersControlApi(token, {
          page,
          limit: FREELANCERS_LIMIT,
          startDay: "2000-01-01",
          endDay: "2100-12-31",
          nome: qApplied,
          priorityStoreId: store.id,
        });

        const items = Array.from(
          new Map(
            (data.items ?? []).map((item) => [
              item.freelancerId,
              {
                id: item.freelancerId,
                nomeCompleto: item.fullName,
                cpf: item.cpf,
              },
            ]),
          ).values(),
        );

        setFreelancers(items);
        setFreelancersTotal(Number((data as any)?.totalItens ?? (data as any)?.total ?? items.length));
      } catch {
        toast.error("Erro ao listar freelancers da loja.");
      } finally {
        setLoadingFreelancers(false);
      }
    },
    [token, store.id, qApplied],
  );

  useEffect(() => {
    if (!isOpen) return;

    setSelectedDayYmd(initialDayYmd);
    setStartTime("09:00");
    setEndTime("18:00");
    setEndManuallyEdited(false);
    setCapacity(1);
    setDraftSlots([]);
    setSaving(false);

    setShowFreelancers(false);
    setFreelancersPage(1);
    setFreelancersTotal(0);
    setFreelancers([]);
    setSelectedFreelancerIds(new Set());
    setQInput("");
    setQApplied("");
    setLoadingFreelancers(false);
  }, [isOpen, initialDayYmd]);

  useEffect(() => {
    if (!isOpen) return;
    if (!showFreelancers) return;
    fetchFreelancersByStore(freelancersPage);
  }, [isOpen, showFreelancers, freelancersPage, fetchFreelancersByStore]);

  const toggleFreelancer = (id: string) => {
    setSelectedFreelancerIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applyFreelancerSearch = useCallback(() => {
    const next = qInput.trim();
    setQApplied(next);
    setFreelancersPage(1);
  }, [qInput]);

  const handleAddDraftSlot = () => {
    const sMin = minutesFromHHMM(startTime);
    const eMin = minutesFromHHMM(endTime);

    if (Number.isNaN(sMin) || Number.isNaN(eMin)) {
      toast.error("Horário inválido.");
      return;
    }
    if (sMin % 30 !== 0 || eMin % 30 !== 0) {
      toast.error("Use horários de meia em meia hora (ex.: 09:00, 09:30, 10:00).");
      return;
    }

    let durationMin = eMin - sMin;
    const crossesMidnight = durationMin <= 0;
    if (crossesMidnight) durationMin += 24 * 60;

    if (durationMin <= 0) {
      toast.error("Fim deve ser maior que início.");
      return;
    }

    if (!capacity || capacity < 1) {
      toast.error("Quantidade de vagas deve ser >= 1.");
      return;
    }

    if (selectedCount > 0 && capacity < selectedCount) {
      toast.error(`Este slot tem ${capacity} vaga(s), mas você selecionou ${selectedCount} freelancer(s).`);
      return;
    }

    const day = selectedDayYmd;
    const endDayYmd = crossesMidnight ? addDaysYmd(selectedDayYmd, 1) : selectedDayYmd;

    const startAt = buildIsoLocalOffset(day, startTime);
    const endAt = buildIsoLocalOffset(endDayYmd, endTime);

    setDraftSlots((prev) => {
      const exists = prev.some((x) => x.day === day && x.startAt === startAt);
      if (exists) {
        toast.error("Já existe um horário selecionado com esse início nesse dia.");
        return prev;
      }
      const next = [...prev, { day, startAt, endAt, capacity }];
      next.sort((a, b) => (a.day === b.day ? a.startAt.localeCompare(b.startAt) : a.day.localeCompare(b.day)));
      return next;
    });
  };

  const removeDraftSlot = (idx: number) => setDraftSlots((prev) => prev.filter((_, i) => i !== idx));

  const canSubmit = useMemo(() => {
    if (saving) return false;
    if (draftSlots.length === 0) return false;
    if (selectedCount > 0 && draftSlots.some((s) => s.capacity < selectedCount)) return false;
    return true;
  }, [saving, draftSlots, selectedCount]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setSaving(true);

      const created: StoreSlot[] = await createSlotsApi(store.id, {
        slots: draftSlots.map((s) => ({ day: s.day, startAt: s.startAt, endAt: s.endAt, capacity: s.capacity })),
      });

      if (selectedCount > 0) {
        if (!bearer) throw new Error("Sessão inválida. Faça login novamente.");
        const ids = Array.from(selectedFreelancerIds);

        const results = await Promise.allSettled(
          created.map((slot) => adminCreateRegistrationsApi(slot.id, bearer, { freelancerIds: ids })),
        );

        let totalCreated = 0;
        let totalSkipped = 0;
        let totalFailed = 0;

        results.forEach((r) => {
          if (r.status === "fulfilled") {
            totalCreated += r.value?.created?.length ?? 0;
            totalSkipped += r.value?.skipped?.length ?? 0;
          } else totalFailed += 1;
        });

        if (totalFailed > 0) toast.warn(`Alguns vínculos falharam em ${totalFailed} slot(s). Verifique regras/vagas.`);
        toast.success(
          `Horários abertos! Vínculos: ${totalCreated} criado(s)` + (totalSkipped ? `, ${totalSkipped} ignorado(s)` : "") + ".",
        );
      } else {
        toast.success("Horários abertos com sucesso!");
      }

      onCreated();
      onClose();
    } catch (err: any) {
      toast.error(err?.message ?? "Erro ao abrir horários.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ModalOverlay" role="presentation" onClick={() => !saving && onClose()}>
      <div className="ModalShell" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="SM_ModalHeader">
          <h2 className="ModalTitle">Abrir Horários</h2>
          <button type="button" className="SM_IconBtn" onClick={() => !saving && onClose()} disabled={saving} title="Fechar">
            ✕
          </button>
        </div>

        <form className="ModalBody" onSubmit={handleSubmit}>
          <div className="SelectedInfoBox">
            <div className="SelectedInfoRow">
              <span className="SelectedInfoLabel">Loja</span>
              <span className="SelectedInfoValue">{store.nomeFantasia}</span>
            </div>
            <div className="SelectedInfoRow">
              <span className="SelectedInfoLabel">Freelancers</span>
              <span className="SelectedInfoValue">
                {selectedCount} selecionado{selectedCount === 1 ? "" : "s"} (opcional)
              </span>
            </div>

            <button
              type="button"
              className="ChangeLink"
              onClick={() => setShowFreelancers((v) => !v)}
              disabled={saving}
            >
              {showFreelancers ? "Ocultar freelancers" : "Selecionar freelancers (opcional)"}
            </button>
          </div>

          {showFreelancers && (
            <div className="Box">
              <div className="BoxTitle">Freelancers</div>

              <div className="FreelancerSearchRow">
                <input
                  className="SlotInputSearch"
                  type="text"
                  value={qInput}
                  onChange={(e) => setQInput(e.target.value)}
                  placeholder="Buscar por nome"
                  disabled={saving}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      applyFreelancerSearch();
                    }
                  }}
                />
                <button type="button" className="FreelancerSearchButton" onClick={applyFreelancerSearch} disabled={saving}>
                  <img src={searchSvg} alt="Buscar" className="FreelancerSearchIcon" />
                </button>
              </div>

              {loadingFreelancers ? (
                <div className="BoxEmpty">Carregando freelancers...</div>
              ) : freelancers.length === 0 ? (
                <div className="BoxEmpty">
                  {qApplied.trim()
                    ? "Nenhum freelancer encontrado para essa busca."
                    : "Nenhum freelancer vinculado a esta loja."}
                </div>
              ) : (
                <div className="DraftList">
                  {freelancers.map((f) => {
                    const checked = selectedFreelancerIds.has(f.id);
                    return (
                      <div key={f.id} className="DraftItem">
                        <div className="DraftInfo" style={{ cursor: saving ? "not-allowed" : "pointer" }}>
                          <div className="DraftTitle" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleFreelancer(f.id)}
                              disabled={saving}
                            />
                            <span>{f.nomeCompleto ?? "Sem nome"}</span>
                          </div>
                          <div className="DraftSub">{f.cpf ? `CPF: ${f.cpf}` : ""}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="Pager" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="PagerButton PagerButtonLight"
                  disabled={loadingFreelancers || freelancersPage <= 1 || saving}
                  onClick={() => setFreelancersPage((p) => Math.max(1, p - 1))}
                >
                  Ant.
                </button>
                <div className="PagerInfo">
                  {freelancersPage} de {freelancersTotalPages}
                </div>
                <button
                  type="button"
                  className="PagerButton"
                  disabled={loadingFreelancers || freelancersPage >= freelancersTotalPages || saving}
                  onClick={() => setFreelancersPage((p) => Math.min(freelancersTotalPages, p + 1))}
                >
                  Próx.
                </button>
              </div>
            </div>
          )}

          <div className="Box">
            <div className="BoxTitle">Selecione o dia e adicione horários</div>

            <div className="DayStrip">
              {daysOfWeek.map((d) => {
                const active = d.ymd === selectedDayYmd;
                return (
                  <button
                    key={d.ymd}
                    type="button"
                    className={`DayPill ${active ? "DayPillActive" : ""}`}
                    onClick={() => setSelectedDayYmd(d.ymd)}
                    disabled={saving}
                  >
                    <div className="DayPillTop">{d.label}</div>
                    <div className="DayPillBottom">{formatBrDate(d.ymd)}</div>
                  </button>
                );
              })}
            </div>

            <div className="SlotFormRow">
              <div className="SlotField">
                <label className="SlotLabel">Início</label>
                <input
                  className="SlotInput"
                  type="time"
                  step={1800}
                  value={startTime}
                  onChange={(e) => {
                    const nextStart = e.target.value;
                    setStartTime(nextStart);
                    if (!endManuallyEdited) setEndTime(hhmmFromMinutes(minutesFromHHMM(nextStart) + 9 * 60));
                  }}
                  disabled={saving}
                />
              </div>

              <div className="SlotField">
                <label className="SlotLabel">Fim</label>
                <input
                  className="SlotInput"
                  type="time"
                  step={1800}
                  value={endTime}
                  onChange={(e) => {
                    setEndTime(e.target.value);
                    setEndManuallyEdited(true);
                  }}
                  disabled={saving}
                />
              </div>

              <div className="SlotField">
                <label className="SlotLabel">Vagas</label>
                <input
                  className="SlotInput"
                  type="number"
                  min={1}
                  value={capacity}
                  onChange={(e) => setCapacity(Math.max(1, Number(e.target.value)))}
                  disabled={saving}
                />
              </div>

              <button type="button" className="SmallActionButton" onClick={handleAddDraftSlot} disabled={saving}>
                Adicionar
              </button>
            </div>

            {selectedCount > 0 && (
              <div className="BoxEmpty" style={{ textAlign: "left" }}>
                Dica: cada horário precisa ter pelo menos {selectedCount} vaga(s) para cadastrar todos os freelancers.
              </div>
            )}

            {draftSlots.length === 0 ? (
              <div className="BoxEmpty">Nenhum horário no rascunho ainda.</div>
            ) : (
              <div className="DraftList">
                {draftSlots.map((s, idx) => (
                  <div key={`${s.day}-${s.startAt}-${idx}`} className="DraftItem">
                    <div className="DraftInfo">
                      <div className="DraftTitle">
                        {formatBrDate(s.day)} • {formatTimeFromIso(s.startAt)} - {formatTimeFromIso(s.endAt)}
                      </div>
                      <div className="DraftSub">{s.capacity} vaga(s)</div>
                    </div>
                    <button type="button" className="SmallDangerButton" onClick={() => removeDraftSlot(idx)} disabled={saving}>
                      Remover
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="ModalActions">
            <button type="button" className="ModalButton ModalButtonCancel" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="ModalButton ModalButtonConfirm" disabled={!canSubmit}>
              {saving ? "Salvando..." : "Confirmar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SlotsManagerPage: React.FC = () => {
  const navigate = useNavigate();

  const token = useSelector((state: RootState) => state.auth.accessToken);
  const bearer = useMemo(() => rawToken(token), [token]);

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
    }
  }, [token, navigate]);

  const [storesPage, setStoresPage] = useState(1);
  const [storesTotal, setStoresTotal] = useState(0);
  const [stores, setStores] = useState<StorePublic[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [storeSearchInput, setStoreSearchInput] = useState("");
  const [storeSearch, setStoreSearch] = useState("");
  const [selectedStore, setSelectedStore] = useState<StoreOption | null>(null);

  const storesTotalPages = useMemo(() => Math.max(1, Math.ceil(storesTotal / STORES_LIMIT)), [storesTotal]);

  const storesWithAllOption = useMemo<StoreOption[]>(
    () => [ALL_STORES_OPTION, ...stores],
    [stores],
  );

  const fetchStores = useCallback(
    async (page: number, nome?: string) => {
      if (!token) return;
      try {
        setLoadingStores(true);
        const trimmed = (nome ?? "").trim();
        const data = await getStoresPaginatedApi(token, {
          page,
          limit: STORES_LIMIT,
          ...(trimmed ? { nome: trimmed } : {}),
        });
        setStores(data.items);
        setStoresTotal(data.total);
      } catch {
        toast.error("Erro ao buscar lojas.");
      } finally {
        setLoadingStores(false);
      }
    },
    [token],
  );

  const applyStoreSearch = () => {
    setStoresPage(1);
    setStoreSearch(storeSearchInput);
  };

  const [weekAnchor, setWeekAnchor] = useState<Date>(() => new Date());
  const weekStart = useMemo(() => startOfWeekMonday(weekAnchor), [weekAnchor]);

  const daysOfWeek = useMemo(() => {
    const labels = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
    return labels.map((label, idx) => {
      const d = addDays(weekStart, idx);
      return { label, ymd: toYmd(d) };
    });
  }, [weekStart]);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);
  const todayYmd = useMemo(() => toYmd(today), [today]);

  const [selectedDayYmd, setSelectedDayYmd] = useState<string>(() => toYmd(new Date()));
  const [includeCanceled] = useState(true);

  const [loadingSlots, setLoadingSlots] = useState(false);
  const [weekSlots, setWeekSlots] = useState<StoreSlotRow[]>([]);

  const [loadingRegs, setLoadingRegs] = useState(false);
  const [regsBySlotId, setRegsBySlotId] = useState<Record<string, any[]>>({});
  const [expandedSlotId, setExpandedSlotId] = useState<string | null>(null);
  const [editedStatusByRegId, setEditedStatusByRegId] = useState<Record<string, SlotRegistrationStatus>>({});
  const [savingByRegId, setSavingByRegId] = useState<Record<string, boolean>>({});

  const refreshWeekSlots = useCallback(async () => {
    if (!selectedStore) {
      setWeekSlots([]);
      setLoadingSlots(false);
      return;
    }

    try {
      setLoadingSlots(true);

      const weekStartYmd = toYmd(weekStart);
      const weekEndYmd = addDaysYmd(weekStartYmd, 7);
      const from = buildIsoUtcRange(weekStartYmd, "00:00");
      const to = buildIsoUtcRange(weekEndYmd, "00:00");

      const data = isAllStores(selectedStore)
        ? await listAllSlotsApi({
            from,
            to,
            includeCanceled,
            onlyAvailable: false,
          })
        : await listStoreSlotsApi(selectedStore.id, {
            from,
            to,
            includeCanceled,
            onlyAvailable: false,
          });

      setWeekSlots([...data].sort((a, b) => a.startAt.localeCompare(b.startAt)));
    } catch {
      toast.error("Erro ao listar horários.");
    } finally {
      setLoadingSlots(false);
    }
  }, [selectedStore, weekStart, includeCanceled]);

  const refreshDayRegistrations = useCallback(async () => {
    if (!selectedStore) {
      setRegsBySlotId({});
      setLoadingRegs(false);
      return;
    }
    if (!token) return;

    try {
      setLoadingRegs(true);

      const regs = isAllStores(selectedStore)
        ? await listAllRegistrationsApi(token, {
            startDay: selectedDayYmd,
            endDay: selectedDayYmd,
          })
        : await listStoreRegistrationsApi(selectedStore.id, token, {
            startDay: selectedDayYmd,
            endDay: selectedDayYmd,
          });

      const map: Record<string, any[]> = {};
      (regs ?? []).forEach((reg: any) => {
        const slotId = reg?.slot?.id ?? reg?.slotId;
        if (!slotId) return;
        if (!map[slotId]) map[slotId] = [];
        map[slotId].push(reg);
      });

      Object.keys(map).forEach((k) => {
        map[k].sort((a: any, b: any) => {
          const an =
            ((a?.freelancer as any)?.nomeCompleto ??
              (a?.freelancer as any)?.fullName ??
              a?.freelancerName ??
              "Sem nome") as string;
          const bn =
            ((b?.freelancer as any)?.nomeCompleto ??
              (b?.freelancer as any)?.fullName ??
              b?.freelancerName ??
              "Sem nome") as string;
          return an.localeCompare(bn);
        });
      });

      setRegsBySlotId(map);
      setEditedStatusByRegId({});
      setSavingByRegId({});
    } catch {
      toast.error("Erro ao listar freelancers cadastrados nos horários.");
      setRegsBySlotId({});
    } finally {
      setLoadingRegs(false);
    }
  }, [selectedStore, token, selectedDayYmd]);

  const [shareLinksBySlotId, setShareLinksBySlotId] = useState<Record<string, { url: string; expiresAt?: string }>>({});
  const [generatingForSlotId, setGeneratingForSlotId] = useState<string | null>(null);

  const handleGenerateLink = async (slot: StoreSlotWithAvailability) => {
    if (!isRealStore(selectedStore)) return;
    if (!bearer) return toast.error("Sessão inválida. Faça login novamente.");
    if (shareLinksBySlotId[slot.id]) return;

    try {
      setGeneratingForSlotId(slot.id);
      const res = await createSlotShareLinkApi(selectedStore.id, bearer, { slotIds: [slot.id] });
      const url = `${window.location.origin}/freelancer/stores/${selectedStore.id}/slots?share=${res.token}`;
      setShareLinksBySlotId((prev) => ({ ...prev, [slot.id]: { url, expiresAt: res.expiresAt } }));
      toast.success("Link gerado!");
    } catch (err: any) {
      toast.error(err?.message ?? "Erro ao gerar link.");
    } finally {
      setGeneratingForSlotId(null);
    }
  };

  const handleCopyLink = async (slotId: string) => {
    const link = shareLinksBySlotId[slotId]?.url;
    if (!link) return;
    try {
      await copyToClipboard(link);
      toast.success("Link copiado!");
    } catch {
      toast.error("Não foi possível copiar. Copie manualmente.");
    }
  };

  const [editingSlotId, setEditingSlotId] = useState<string | null>(null);
  const [editStartTime, setEditStartTime] = useState("09:00");
  const [editEndTime, setEditEndTime] = useState("18:00");
  const [editCapacity, setEditCapacity] = useState<number>(1);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingSlotId, setDeletingSlotId] = useState<string | null>(null);

  const startEdit = (slot: StoreSlotWithAvailability) => {
    setEditingSlotId(slot.id);
    setEditStartTime(formatTimeFromIso(slot.startAt));
    setEditEndTime(formatTimeFromIso(slot.endAt));
    setEditCapacity(slot.capacity);
  };

  const cancelEdit = () => {
    setEditingSlotId(null);
    setSavingEdit(false);
  };

  const saveEdit = async (slot: StoreSlotWithAvailability) => {
    if (!isRealStore(selectedStore)) return;

    const sMin = minutesFromHHMM(editStartTime);
    const eMin = minutesFromHHMM(editEndTime);

    if (Number.isNaN(sMin) || Number.isNaN(eMin)) return toast.error("Horário inválido.");
    if (sMin % 30 !== 0 || eMin % 30 !== 0)
      return toast.error("Use horários de meia em meia hora (ex.: 09:00, 09:30, 10:00).");

    let durationMin = eMin - sMin;
    const crossesMidnight = durationMin <= 0;
    if (crossesMidnight) durationMin += 24 * 60;
    if (durationMin <= 0) return toast.error("Fim deve ser maior que início.");

    if (!editCapacity || editCapacity < 1) return toast.error("Capacidade deve ser >= 1.");
    const occupied = slot.occupied ?? 0;
    if (editCapacity < occupied)
      return toast.error(`A capacidade não pode ser menor que o número de agendados (${occupied}).`);

    const slotDayYmd = ymdFromIsoOrYmd(slot.day ?? slot.startAt);
    const endDayYmd = crossesMidnight ? addDaysYmd(slotDayYmd, 1) : slotDayYmd;

    const startAt = buildIsoLocalOffset(slotDayYmd, editStartTime);
    const endAt = buildIsoLocalOffset(endDayYmd, editEndTime);

    try {
      setSavingEdit(true);
      await updateSlotApi(selectedStore.id, slot.id, { day: slotDayYmd, startAt, endAt, capacity: editCapacity });
      toast.success("Horário atualizado!");
      setEditingSlotId(null);
      await refreshWeekSlots();
    } catch {
      toast.error("Erro ao atualizar horário.");
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteSlot = async (slot: StoreSlotWithAvailability) => {
    if (!isRealStore(selectedStore)) return;
    if (!window.confirm("Deseja excluir este horário?")) return;

    try {
      setDeletingSlotId(slot.id);
      await deleteSlotApi(selectedStore.id, slot.id);
      toast.success("Horário excluído!");
      if (editingSlotId === slot.id) setEditingSlotId(null);
      if (expandedSlotId === slot.id) setExpandedSlotId(null);
      setShareLinksBySlotId((prev) => {
        const next = { ...prev };
        delete next[slot.id];
        return next;
      });
      await refreshWeekSlots();
    } catch {
      toast.error("Erro ao excluir horário.");
    } finally {
      setDeletingSlotId(null);
    }
  };

  const [openDialog, setOpenDialog] = useState(false);

  const slotsByDay = useMemo(() => {
    const map: Record<string, StoreSlotRow[]> = {};
    for (const s of weekSlots) {
      const dayYmd = new Date(s.startAt).toLocaleDateString("en-CA");
      if (!map[dayYmd]) map[dayYmd] = [];
      map[dayYmd].push(s);
    }
    for (const k of Object.keys(map)) map[k].sort((a, b) => a.startAt.localeCompare(b.startAt));
    return map;
  }, [weekSlots]);

  const selectedDaySlots = useMemo(() => slotsByDay[selectedDayYmd] ?? [], [slotsByDay, selectedDayYmd]);

  const weekTotals = useMemo(() => {
    const total = weekSlots.length;
    const canceled = weekSlots.filter((s) => s.isCanceled).length;
    const withOccupied = weekSlots.filter((s) => (s.occupied ?? 0) > 0).length;
    return { total, canceled, withOccupied };
  }, [weekSlots]);

  const normalizePresence = (s: any): SlotRegistrationStatus => {
    const up = String(s ?? "PENDENTE").toUpperCase();
    if (up === "APROVADO") return "APROVADO";
    if (up === "AUSENTE") return "AUSENTE";
    if (up === "CANCELADO") return "CANCELADO";
    return "PENDENTE";
  };

  const PRESENCE_STATUS_OPTIONS: SlotRegistrationStatus[] = ["PENDENTE", "APROVADO", "AUSENTE", "CANCELADO"];

  const handleChangePresence = (regId: string, next: SlotRegistrationStatus) => {
    setEditedStatusByRegId((prev) => ({ ...prev, [regId]: next }));
  };

  const handleConfirmPresence = async (slotId: string, regId: string) => {
    if (!token) return;

    const list = regsBySlotId[slotId] ?? [];
    const reg = list.find((r: any) => (r?.id ?? "") === regId);

    if (!reg) return toast.error("Inscrição não encontrada.");

    const current = normalizePresence(reg?.status);
    const target = (editedStatusByRegId[regId] ?? current) as SlotRegistrationStatus;

    if (String(current) === String(target)) {
      setEditedStatusByRegId((prev) => {
        const cp = { ...prev };
        delete cp[regId];
        return cp;
      });
      return toast.info("Nenhuma alteração para salvar.");
    }

    try {
      setSavingByRegId((p) => ({ ...p, [regId]: true }));
      const dto: UpdateRegistrationStatusDto = { status: target };
      await updateRegistrationStatusApi(regId, token, dto);

      toast.success("Status atualizado!");

      setRegsBySlotId((prev) => {
        const cp = { ...prev };
        const arr = [...(cp[slotId] ?? [])];
        const idx = arr.findIndex((r: any) => (r?.id ?? "") === regId);
        if (idx >= 0) arr[idx] = { ...arr[idx], status: target };
        cp[slotId] = arr;
        return cp;
      });

      setEditedStatusByRegId((prev) => {
        const cp = { ...prev };
        delete cp[regId];
        return cp;
      });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data?.error ??
        err?.data?.message ??
        err?.message;

      toast.error((Array.isArray(msg) ? msg.join(", ") : String(msg ?? "")).trim() || "Erro ao atualizar status.");
    } finally {
      setSavingByRegId((p) => ({ ...p, [regId]: false }));
    }
  };

  useEffect(() => {
    if (!token) return;
    if (selectedStore) return;
    fetchStores(storesPage, storeSearch);
  }, [token, selectedStore, storesPage, storeSearch, fetchStores]);

  useEffect(() => {
    if (!selectedStore) return;
    const inWeek = daysOfWeek.some((d) => d.ymd === selectedDayYmd);
    if (!inWeek) setSelectedDayYmd(daysOfWeek[0].ymd);
  }, [selectedStore, daysOfWeek, selectedDayYmd]);

  useEffect(() => {
    if (!selectedStore) return;
    setEditingSlotId(null);
    refreshWeekSlots();
  }, [selectedStore, weekStart, includeCanceled, refreshWeekSlots]);

  useEffect(() => {
    if (!selectedStore) return;
    setExpandedSlotId(null);
    refreshDayRegistrations();
  }, [selectedStore, selectedDayYmd, refreshDayRegistrations]);

  const pickStore = (s: StoreOption) => {
    setSelectedStore(s);
    setWeekAnchor(today);
    const inWeek = daysOfWeek.some((d) => d.ymd === todayYmd);
    setSelectedDayYmd(inWeek ? todayYmd : daysOfWeek[0].ymd);
    setShareLinksBySlotId({});
    setRegsBySlotId({});
    setExpandedSlotId(null);
  };

  const changeStore = () => {
    setSelectedStore(null);
    setWeekSlots([]);
    setShareLinksBySlotId({});
    setEditingSlotId(null);
    setRegsBySlotId({});
    setExpandedSlotId(null);
    setStoresPage(1);
    setStoreSearchInput("");
    setStoreSearch("");
  };

  if (!token) return <div className="SM_Page"><div className="BoxEmpty">Faça login para continuar.</div></div>;

  return (
    <div className="homeLayout">
      <main className="homeMain">
        <div className="StoreMenuPage SlotsManagerPage ecosystem">
          <img
            className="ecosystem__bg-img"
            src={DenseForest}
            alt=""
            aria-hidden="true"
          />
          <div className="ecosystem__content-bg" aria-hidden="true" />

          <div className="freelancer-container ecosystem__content">
            <header className="ecosystem__header">
              <h1 className="ecosystem__title">
                <span className="ecosystem__title-strong">Gestão de </span>
                <span className="ecosystem__title-light">Horários</span>
              </h1>

              <p className="ecosystem__subtitle">Abrir, visualizar e atualizar horários</p>

              <div className="ecosystem__actions">
                <button
                  className="ecosystem__actionBtn"
                  onClick={() => navigate(OPERACOES_PAGE())}
                  disabled={loadingStores || savingEdit || loadingSlots}
                >
                  Operações
                </button>
              </div>
            </header>

            <div className="freelancer-card-table">
              <div className="SlotsTopBar">
                <div className="SlotsTopBarLeft mb-10">
                  {selectedStore ? (
                    <>
                      <button
                        type="button"
                        className="PagerButton PagerButtonLight ml-10"
                        onClick={() => setOpenDialog(true)}
                        disabled={savingEdit || loadingSlots || !isRealStore(selectedStore)}
                      >
                        Abrir horários
                      </button>

                      <button
                        type="button"
                        className="PagerButton PagerButtonLight ml-10"
                        onClick={changeStore}
                        disabled={savingEdit}
                      >
                        Trocar loja
                      </button>
                    </>
                  ) : null}
                </div>
              </div>

              {!selectedStore ? (
                <div className="Box">
                  <div className="SlotsTitleRow">
                    <div className="BoxTitle" style={{ margin: 0 }}>
                      Selecione uma loja
                    </div>
                  </div>

                  <div className="FreelancerSearch FreelancerSearch--mobileWrap">
                    <input
                      className="freelancer-input FreelancerSearchInput"
                      value={storeSearchInput}
                      onChange={(e) => setStoreSearchInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          applyStoreSearch();
                        }
                      }}
                      disabled={loadingStores}
                      placeholder="Nome da loja"
                    />

                    <button
                      type="button"
                      className="PagerButton FreelancerSearchBtn"
                      onClick={applyStoreSearch}
                      disabled={loadingStores}
                      title="Buscar"
                    >
                      <img className="FreelancerSearchIcon" src={searchSvg} alt="Buscar" />
                    </button>
                  </div>

                  {loadingStores ? (
                    <div className="BoxEmpty">Carregando lojas...</div>
                  ) : (
                    <div className="List">
                      {storesWithAllOption.map((s) => (
                        <button key={s.id ?? "all-stores"} type="button" className="ListItem" onClick={() => pickStore(s)}>
                          <div className="ListItemTitle">{s.nomeFantasia}</div>
                          <div className="ListItemSub">{s.endereco ? s.endereco : "Sem endereço"}</div>
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="Pager">
                    <button
                      type="button"
                      className="PagerButton PagerButtonLight"
                      disabled={loadingStores || storesPage <= 1}
                      onClick={() => setStoresPage((p) => Math.max(1, p - 1))}
                    >
                      Ant.
                    </button>

                    <div className="PagerInfo">
                      {storesPage} de {storesTotalPages}
                    </div>

                    <button
                      type="button"
                      className="PagerButton"
                      disabled={loadingStores || storesPage >= storesTotalPages}
                      onClick={() => setStoresPage((p) => Math.min(storesTotalPages, p + 1))}
                    >
                      Próx.
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="SelectedInfoBox">
                    <div className="SelectedInfoRow">
                      <span className="SelectedInfoLabel">Loja</span>
                      <span className="SelectedInfoValue">{selectedStore.nomeFantasia}</span>
                    </div>

                    <div className="SelectedInfoRow">
                      <span className="SelectedInfoLabel">Endereço</span>
                      <span className="SelectedInfoValue">
                        {selectedStore.endereco ? selectedStore.endereco : "Sem endereço"}
                      </span>
                    </div>
                  </div>

                  <div className="WeekHeader">
                    <button
                      type="button"
                      className="PagerButton PagerButtonLight"
                      onClick={() => setWeekAnchor((d) => addDays(d, -7))}
                      disabled={savingEdit}
                    >
                      Semana anterior
                    </button>

                    <div className="WeekTitle">
                      Semana de <b>{formatBrDate(toYmd(weekStart))}</b>
                    </div>

                    <button
                      type="button"
                      className="PagerButton PagerButtonLight"
                      onClick={() => setWeekAnchor((d) => addDays(d, 7))}
                      disabled={savingEdit}
                    >
                      Próxima semana
                    </button>
                  </div>

                  <div className="SlotsMetaRow">
                    <div className="SlotsMetaText amplesoft-regular mb-10">
                      Total semana: <b>{weekTotals.total}</b> • Agendados: <b>{weekTotals.withOccupied}</b> • Cancelados:{" "}
                      <b>{weekTotals.canceled}</b>
                    </div>
                  </div>

                  <div className="DayStrip">
                    {daysOfWeek.map((d) => {
                      const active = d.ymd === selectedDayYmd;

                      return (
                        <button
                          key={d.ymd}
                          type="button"
                          className={`DayPill ${active ? "DayPillActive" : ""}`}
                          onClick={() => setSelectedDayYmd(d.ymd)}
                          disabled={savingEdit}
                        >
                          <div className="DayPillTop">{d.label}</div>
                          <div className="DayPillBottom">{formatBrDate(d.ymd)}</div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="Box">
                    <div className="BoxTitle">
                      Horários do dia <b>{formatBrDate(selectedDayYmd)}</b>
                    </div>

                    {loadingSlots ? (
                      <div className="BoxEmpty">Carregando horários...</div>
                    ) : selectedDaySlots.length === 0 ? (
                      <div className="BoxEmpty">Nenhum horário encontrado para este dia.</div>
                    ) : (
                      <div className="RatingsTableWrap SlotsTableWrap">
                        <table className="RatingsTable">
                          <thead>
                            <tr>
                              <th className="amplesoft">Loja</th>
                              <th className="amplesoft">Início</th>
                              <th className="amplesoft">Fim</th>
                              <th className="amplesoft">Ocupado</th>
                              <th className="amplesoft">Status</th>
                              <th className="amplesoft">Freelancers</th>
                              <th className="amplesoft">Link</th>
                              <th className="amplesoft">Ações</th>
                            </tr>
                          </thead>

                          <tbody>
                            {selectedDaySlots.map((slot) => {
                              const isEditing = editingSlotId === slot.id;

                              const regs = regsBySlotId[slot.id] ?? [];
                              const isExpanded = expandedSlotId === slot.id;

                              const ymdSP = (iso: string) =>
                                new Intl.DateTimeFormat("en-CA", {
                                  timeZone: "America/Sao_Paulo",
                                  year: "numeric",
                                  month: "2-digit",
                                  day: "2-digit",
                                }).format(new Date(iso));

                              const endIsNextDay = ymdSP(slot.endAt) !== ymdSP(slot.startAt);
                              const endLabel = endIsNextDay ? `${formatTimeFromIso(slot.endAt)} (+1)` : formatTimeFromIso(slot.endAt);

                              const future = new Date(slot.startAt).getTime() > Date.now();

                              return (
                                <React.Fragment key={slot.id}>
                                  <tr className={slot.isCanceled ? "SlotsRowCanceled" : undefined}>
                                    <td className="RatingsCell">
                                      {slot.store?.nomeFantasia ?? selectedStore.nomeFantasia}
                                    </td>
                                    <td className="RatingsCell">
                                      {isEditing ? (
                                        <input
                                          className="freelancer-input SlotsTimeInput"
                                          type="time"
                                          step={1800}
                                          value={editStartTime}
                                          onChange={(e) => setEditStartTime(e.target.value)}
                                          disabled={savingEdit}
                                        />
                                      ) : (
                                        formatTimeFromIso(slot.startAt)
                                      )}
                                    </td>

                                    <td className="RatingsCell">
                                      {isEditing ? (
                                        <input
                                          className="freelancer-input SlotsTimeInput"
                                          type="time"
                                          step={1800}
                                          value={editEndTime}
                                          onChange={(e) => setEditEndTime(e.target.value)}
                                          disabled={savingEdit}
                                        />
                                      ) : (
                                        endLabel
                                      )}
                                    </td>

                                    <td className="RatingsCell">
                                      {(slot.occupied ?? 0)}/{slot.totalCapacity}
                                    </td>

                                    <td className="RatingsCell">
                                      <div className="SlotsBadges">
                                        {slot.isCanceled ? (
                                          <span className="SlotsBadge">Cancelado</span>
                                        ) : slot.isFull || (slot.available ?? 0) <= 0 ? (
                                          <span className="SlotsBadge SlotsBadge--warn">Lotado</span>
                                        ) : (
                                          <span className="SlotsBadge SlotsBadge--ok">Com vagas</span>
                                        )}
                                      </div>
                                    </td>

                                    <td className="RatingsCell">
                                      {loadingRegs ? (
                                        <span className="SlotsLinkInfo">Carregando...</span>
                                      ) : regs.length === 0 ? (
                                        <span className="SlotsLinkInfo">-</span>
                                      ) : (
                                        <button
                                          type="button"
                                          className="PagerButton PagerButtonLight"
                                          onClick={() => setExpandedSlotId((prev) => (prev === slot.id ? null : slot.id))}
                                          disabled={savingEdit}
                                        >
                                          Ver
                                        </button>
                                      )}
                                    </td>

                                    <td className="RatingsCell">
                                      {shareLinksBySlotId[slot.id]?.url ? (
                                        <button
                                          type="button"
                                          className="PagerButton PagerButtonLight"
                                          onClick={() => handleCopyLink(slot.id)}
                                          disabled={savingEdit}
                                        >
                                          Copiar
                                        </button>
                                      ) : slot.isCanceled ? (
                                        <span className="SlotsLinkInfo">Cancelado</span>
                                      ) : !future ? (
                                        <span className="SlotsLinkInfo">Encerrado</span>
                                      ) : (
                                        <button
                                          type="button"
                                          className="PagerButton PagerButtonLight"
                                          onClick={() => handleGenerateLink(slot)}
                                          disabled={savingEdit || generatingForSlotId === slot.id}
                                        >
                                          {generatingForSlotId === slot.id ? "Gerando..." : "Gerar"}
                                        </button>
                                      )}
                                    </td>

                                    <td className="RatingsCell SlotsActionsCell">
                                      {!isEditing ? (
                                        <div className="SlotsActionsBtns">
                                          <button
                                            type="button"
                                            className="PagerButton"
                                            onClick={() => startEdit(slot)}
                                            disabled={savingEdit || deletingSlotId === slot.id || slot.isCanceled}
                                          >
                                            Editar
                                          </button>

                                          <button
                                            type="button"
                                            className="PagerButton PagerButtonLight"
                                            onClick={() => deleteSlot(slot)}
                                            disabled={savingEdit || deletingSlotId === slot.id || slot.isCanceled}
                                          >
                                            {deletingSlotId === slot.id ? "Excluindo..." : "Excluir"}
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="SlotsActionsBtns">
                                          <button
                                            type="button"
                                            className="PagerButton PagerButtonLight"
                                            onClick={cancelEdit}
                                            disabled={savingEdit || deletingSlotId === slot.id}
                                          >
                                            Cancelar
                                          </button>

                                          <button
                                            type="button"
                                            className="PagerButton"
                                            onClick={() => saveEdit(slot)}
                                            disabled={savingEdit || deletingSlotId === slot.id || slot.isCanceled}
                                          >
                                            {savingEdit ? "Salvando..." : "Salvar"}
                                          </button>
                                        </div>
                                      )}
                                    </td>
                                  </tr>

                                  {isExpanded ? (
                                    <tr className={slot.isCanceled ? "SlotsRowCanceled" : undefined}>
                                      <td className="RatingsCell" colSpan={8}>
                                        {regs.length === 0 ? (
                                          <div className="BoxEmpty">Nenhum freelancer cadastrado neste horário.</div>
                                        ) : (
                                          <div className="DraftList">
                                            {regs.map((reg: any) => {
                                              const regId = String(reg?.id ?? "");
                                              const name =
                                                (reg?.freelancer as any)?.nomeCompleto ??
                                                (reg?.freelancer as any)?.fullName ??
                                                reg?.freelancerName ??
                                                "Sem nome";
                                              const cpf = (reg?.freelancer as any)?.cpf ?? reg?.freelancerCpf ?? "";

                                              const current = normalizePresence(reg?.status);
                                              const target = (regId ? (editedStatusByRegId[regId] ?? current) : current) as SlotRegistrationStatus;
                                              const dirty = regId ? String(current) !== String(target) : false;
                                              const saving = regId ? !!savingByRegId[regId] : false;

                                              const key =
                                                reg?.id ??
                                                `${slot.id}-${((reg?.freelancer as any)?.id ?? reg?.freelancerId ?? name) as string}`;

                                              return (
                                                <div key={key} className="DraftItem">
                                                  <div className="DraftInfo">
                                                    <div className="DraftTitle">{name}</div>
                                                    <div className="DraftSub">{cpf ? `CPF: ${cpf}` : ""}</div>
                                                  </div>

                                                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                    <select
                                                      className="freelancer-input"
                                                      value={target}
                                                      onChange={(e) =>
                                                        regId && handleChangePresence(regId, e.target.value as SlotRegistrationStatus)
                                                      }
                                                      disabled={savingEdit || saving || slot.isCanceled}
                                                      style={{ borderColor: dirty ? "#f39c12" : undefined }}
                                                    >
                                                      {PRESENCE_STATUS_OPTIONS.map((s) => (
                                                        <option key={s} value={s}>
                                                          {s}
                                                        </option>
                                                      ))}
                                                    </select>

                                                    <button
                                                      type="button"
                                                      className="PagerButton"
                                                      onClick={() => handleConfirmPresence(slot.id, regId)}
                                                      disabled={savingEdit || saving || slot.isCanceled || !regId || !dirty}
                                                    >
                                                      {saving ? "Salvando..." : "Salvar"}
                                                    </button>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  ) : null}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {isRealStore(selectedStore) ? (
                    <OpenSlotsDialog
                      isOpen={openDialog}
                      onClose={() => setOpenDialog(false)}
                      store={selectedStore}
                      token={token}
                      bearer={bearer}
                      daysOfWeek={daysOfWeek}
                      initialDayYmd={selectedDayYmd}
                      onCreated={async () => {
                        setShareLinksBySlotId({});
                        await refreshWeekSlots();
                        await refreshDayRegistrations();
                      }}
                    />
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SlotsManagerPage;
